/**
 * useLegalLinks — Fetches legal page URLs from /api/public/legal
 *
 * Consumes the public Legal API (sl-ycf7.6.1) and returns typed links.
 * Uses app language (i18n) for consistent localization.
 *
 * @see sl-ycf7.6.3
 */

import { useI18n } from '@affine/i18n';
import { useEffect, useState } from 'react';

export interface LegalLink {
  url: string;
  label: string;
}

export interface LegalLinks {
  terms?: LegalLink;
  privacy?: LegalLink;
  imprint?: LegalLink;
  avv?: LegalLink;
  withdrawal?: LegalLink;
}

export type LegalLanguage = 'de' | 'en';

const LEGAL_ORIGIN = 'https://streamlain.de';

const LEGAL_LINKS: Record<LegalLanguage, Required<LegalLinks>> = {
  de: {
    imprint: { url: `${LEGAL_ORIGIN}/de/impressum/`, label: 'Impressum' },
    privacy: {
      url: `${LEGAL_ORIGIN}/de/datenschutz/`,
      label: 'Datenschutzerklärung',
    },
    terms: { url: `${LEGAL_ORIGIN}/de/agb/`, label: 'AGB' },
    avv: {
      url: `${LEGAL_ORIGIN}/de/avv/`,
      label: 'Auftragsverarbeitungsvertrag',
    },
    withdrawal: {
      url: `${LEGAL_ORIGIN}/de/widerruf/`,
      label: 'Widerrufsbelehrung',
    },
  },
  en: {
    imprint: { url: `${LEGAL_ORIGIN}/en/imprint/`, label: 'Imprint' },
    privacy: {
      url: `${LEGAL_ORIGIN}/en/privacy/`,
      label: 'Privacy Policy',
    },
    terms: {
      url: `${LEGAL_ORIGIN}/en/terms/`,
      label: 'Terms of Service',
    },
    avv: {
      url: `${LEGAL_ORIGIN}/en/dpa/`,
      label: 'Data Processing Agreement',
    },
    withdrawal: {
      url: `${LEGAL_ORIGIN}/en/withdrawal/`,
      label: 'Withdrawal Policy',
    },
  },
};

export function getLegalLinks(language: LegalLanguage): Required<LegalLinks> {
  return LEGAL_LINKS[language];
}

export function mergeLegalLinks(
  language: LegalLanguage,
  links?: LegalLinks | null
): Required<LegalLinks> {
  const canonical = getLegalLinks(language);
  return Object.fromEntries(
    Object.entries(canonical).map(([key, fallback]) => [
      key,
      {
        ...fallback,
        label: links?.[key as keyof LegalLinks]?.label || fallback.label,
      },
    ])
  ) as Required<LegalLinks>;
}

export function useLegalLinks(): Required<LegalLinks> {
  const t = useI18n();

  // Use the app language (i18n) instead of navigator.language so that
  // legal link labels match the rest of the UI (consent text, buttons, etc.)
  const lang: LegalLanguage = t.language?.startsWith('en') ? 'en' : 'de';
  const [remote, setRemote] = useState<{
    language: LegalLanguage;
    links: LegalLinks;
  } | null>(null);

  useEffect(() => {
    const controller = new AbortController();

    fetch(`/api/public/legal?lang=${lang}`, { signal: controller.signal })
      .then(res => (res.ok ? res.json() : null))
      .then(data => {
        if (data?.links) {
          setRemote({ language: lang, links: data.links });
        }
      })
      .catch(() => {
        // Silently fail — legal links are enhancement, not critical
      });

    return () => controller.abort();
  }, [lang]);

  return mergeLegalLinks(lang, remote?.language === lang ? remote.links : null);
}
